import { DocumentCheckIcon, ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { FlexAuto, FlexRow } from './layout';
import clsx from 'clsx';
import { PrimerStyle } from './form';
import { inter as baseFont } from '@/ui/core/fonts';

const NBSP = "\u00A0";

export function ContentRoot({ children }: React.PropsWithChildren) {
  return (<>
    <div className={clsx(baseFont.className, "antialiased select-none")}>
      {children}
    </div>
  </>);
}

export function SimplePage({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <ContentPage className={className}>
      <SimpleRow>
        {children}
      </SimpleRow>
    </ContentPage>
  );
}
export function SimpleRow({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <ContentRow className={className}>
      <ContentLeft />
      <ContentCenter>
        {children}
      </ContentCenter>
      <ContentRight />
    </ContentRow>
  );
}
export function FullRow({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <ContentRow className={className}>
      <ContentFull>
      {children}
      </ContentFull>
    </ContentRow>
  );
}

export function ContentPage({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <main className={`${className}`}>
      {children}
    </main>
  );
}

export function ContentRow({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <div className={`flex flex-row items-stretch ${className}`}>
      <FlexAuto />
      {children}
      <FlexAuto />
    </div>
  );
}
export function ContentFull({ className, children = NBSP }: { className?: string, children?: React.ReactNode }) {
  return (
    <div className={`flex-none w-[90rem] min-w-[90rem] ${className}`}>
      {children}
    </div>
  );
}
export function ContentLeft({ className, children = NBSP }: { className?: string, children?: React.ReactNode }) {
  return (
    <div className={`flex-none w-[20rem] min-w-[20rem] ${className}`}>
      {children}
    </div>
  );
}
export function ContentCenter({ className, children = NBSP }: { className?: string, children?: React.ReactNode }) {
  return (
    <div className={`flex-auto w-[50rem] max-w-[50rem] ${className}`}>
      {children}
    </div>
  );
}
export function ContentRight({ className, children = NBSP }: { className?: string, children?: React.ReactNode }) {
  return (
    <div className={`flex-none w-[20rem] min-w-[20rem] ${className}`}>
      {children}
    </div>
  );
}

export function PreHeader({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <p className={`text-xl font-medium mt-8 mb-1 ${className}`}>
      {children}
    </p>
  );
}
export function Header({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <h1 className={`text-4xl font-medium mt-10 mb-10 ${className}`}>
      {children}
    </h1>
  );
}
export function Header1({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <h1 className={`text-4xl font-medium ${className}`}>
      {children}
    </h1>
  );
}
export function Header2({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <h1 className={`text-3xl text-slate-600 font-medium mt-10 mb-2 ${className}`}>
      {children}
    </h1>
  );
}
export function Header3({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <h1 className={clsx(className, "text-title-lg text-deep-blue mt-10 mb-2")}>
      {children}
    </h1>
  );
}

export function CollapsedHeader({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <h1 className={`text-4xl font-medium mt-10 mb-10 ${className}`}>
      {children}
    </h1>
  );
}
export function CollapsedHeader2({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <h1 className={`text-3xl font-medium mt-6 mb-1 text-gray-500/75 ${className}`}>
      {children}
    </h1>
  );
}
export function StickyHeader({ className, children }: React.PropsWithChildren<{ className?: string }>) {
  return (
    <FullRow className={clsx(className, "sticky top-0 bg-white z-10")}>
      {children}
    </FullRow>
  );
}

export function Text({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <p className={clsx(className, "text-body-md text-cool-gray mb-4")}>
      {children}
    </p>
  );
}

export function Prompt({ children }: { children?: React.ReactNode }) {
  return (
    <p className="text-body-md text-cool-gray mt-1 mb-3">
      {children}
    </p>
  );
}

export function Label({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <label className={`text-label-lg text-cool-gray mt-10 mb-1 ${className}`}>
      {children}
    </label>
  );
}
export function NoLabel() {
  return (
    <div className="mt-10 h-0">&nbsp;</div>
  );
}
export function Superscript({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <sup className={`font-features sups ${className}`}>
      {children}
    </sup>
  );
}
export function Subscript({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <sub className={`font-features subs ${className}`}>
      {children}
    </sub>
  );
}
export function Option({ className, children }: { className?: string, children?: React.ReactNode }) {
  return (
    <p className={`text-lg font-light ml-10 mt-1 mb-1 ${className}`}>
      {children}
    </p>
  );
}
export function ReminderBox({ type: _type, className, children }: {
  type?:      "info" | "warning" | "error" | "success",
  className?: string,
  children?:  React.ReactNode
}) {
  const type = _type || "info";
  return (
    <div className={clsx(
      "text-lg font-normal mt-4 mb-0 rounded-xl px-4 py-3",
      type === "info"    && "border border-dashed border-slate-400 text-slate-600",
      type === "warning" && "bg-gray-300/50 text-gray-800",
      type === "error"   && "bg-red-200     text-gray-800",
      type === "success" && "bg-teal-500/50 text-gray-800",
      className,
    )}>
      {/* bg-green-200/50 */}
      <FlexRow>
        <FlexAuto />
        {type === "info"    && <InformationCircleIcon   className="mr-1 mt-1 h-5 w-5" />}
        {type === "warning" && <ExclamationTriangleIcon className="mr-1 mt-1 h-5 w-5" />}
        {type === "error"   && <XCircleIcon             className="mr-1 mt-1 h-5 w-5" />}
        {type === "success" && <DocumentCheckIcon       className="mr-1 mt-1 h-5 w-5" />}
        <div>{children}</div>
        <FlexAuto />
      </FlexRow>
    </div>
  );
}
export function InputPrimer({ style, className, children }: {
  style?:     PrimerStyle,
  className?: string,
  children?:  string
}) {
  return (
    <Text className={className}>
      {style === undefined     && <span><RequiredAsterisk /> Required information</span>                    }
      {style === "prefix"      && <span>{children} (<RequiredAsterisk /> Required information)</span>       }
      {style === "suffix"      && <span>Enter required information (<RequiredAsterisk />) {children}</span> }
      {style === "instruction" && <span>{children}</span>                                                   }
    </Text>
  );
}
export function RequiredAsterisk() {
  return <>
    <span className="text-burnt-orange">*</span>
  </>;
}
